import React from 'react';
import Header from './Header';
import * as S from './styles';

const Main: React.FC = () => {
  return (
    <S.Container>
      <Header />
      <S.Wrapper>
        <S.Content className="left">
          <S.Photo1
            className="wow animate__animated animate__zoomIn"
            data-wow-duration="1s"
          />
          <S.Legend>
            Top, Saint Laurent. Brincos, Messika. Colar, Underground England.
          </S.Legend>
        </S.Content>
        <S.Content className="right text">
          <S.Text
            className="wow animate__animated animate__fadeIn"
            data-wow-duration="1s"
            data-wow-delay="400ms"
          >
            <S.Title>Swinging 80’s</S.Title>
            <p>
              Se a juventude inglesa da década de 1960 viveu a Beatlemania, os
              jovens dali, nos anos 1980, se jogaram com o mesmo fôlego em um
              novo momento pop, agora com um visual muito mais edgy com
              influências do punk. Antes de serem batizados como new romantics
              pela revista britânica <i>Sounds</i> (1970-1991), esse movimento
              também ganhou os apelidos de New Dandies, Romantic Rebels e Blitz
              Kids.
            </p>

            <p>
              A Blitz, no caso, era uma boate no bairro de Covent Garden, em
              Londres. A partir de 1978, nas terças-feiras, a festa chamada
              Bowie Nights era frequentada por artistas da música (como Boy
              George, Marc Almond e Soft Cell), da moda (caso de Stephen Jones,
              John Galliano e Zandra Rhodes) e do meio editorial, porque foi
              nesse contexto que revistas icônicas como <i>The Face</i> e{' '}
              <i>i-D</i> surgiram.{' '}
            </p>

            <p>
              O que tocava? Além de, óbvio, David Bowie, tracks de Giorgio
              Moroder, Kraftwerk, Roxy Music eram alguns dos sons que bombavam
              nas caixas da casa. A música-tema do evento era “Heroes”, do
              Bowie, porque, pelo menos por uma noite, as pessoas poderiam se
              arrumar e ser quem elas quisessem. Não demorou muito para que esse
              sentimento de liberdade invadisse o espírito do tempo, dando
              espaço às bandas daquele circuito no mainstream.{' '}
            </p>

            <p>
              Exemplo disso é o cantor galês Steve Strange, que organizava
              festas enquanto tocava com a sua banda, a Visage. Mas, além dele,
              outros grupos se tornaram muito populares conforme os anos
              passaram: Duran Duran, The Human League, Depeche Mode, Bananarama
              e Wham!, entre muitos outros.{' '}
            </p>

            <p>
              Em 1982, ano de lançamento da MTV nos Estados Unidos, a
              programação do novo canal contava com bandas britânicas e é claro
              que isso teve um impacto enorme nos charts. Quase metade dos
              grupos mais ouvidos do país tinha sotaque do outro lado do oceano.
              O momento foi até apelidado de Segunda Invasão Britânica.
            </p>
          </S.Text>
        </S.Content>
      </S.Wrapper>
      <S.Wrapper className="right">
        <S.Content className="left text">
          <S.Text
            className="right wow animate__animated animate__fadeIn"
            data-wow-duration="1s"
            data-wow-delay="400ms"
          >
            <S.Title>Logo aqui</S.Title>
            <p>
              “O RPM e todos os new romantics somos filhos do rock do começo dos
              anos 1970, entre o glam rock e o punk”, comentou Paulo Ricardo à{' '}
              <a
                href="https://m.folha.uol.com.br/ilustrada/2013/03/1251064-nos-bastidores-da-spfw-paulo-ricardo-responde-a-criticas-ao-seu-visual-bowie.shtml"
                target="_blank"
                rel="noreferrer"
              >
                <i>Folha de S.Paulo</i>
              </a>
              , em 2013. É verdade, o grupo surgiu em 1983 muito inspirado pelo
              som que tocava lá fora e, dessa forma, também tentava assimilar o
              look com peças de alfaiataria, sem nunca deixar de lado o lápis
              preto no olho.
            </p>

            <p>
              No mesmo ano da estreia dos brasileiros, porém, do outro lado do
              mundo, na Austrália, o grupo Real Life também fazia o seu début.
              Acompanhados do som dos sintetizadores, era um novo momento para
              falar sobre amor, ainda que muito influenciados pelo caldeirão
              artístico da década.{' '}
            </p>

            <p>
              No Brasil, o pop romântico dos anos 1980 extrapolava a estética
              britânica para criar o seu próprio som, com letras sobre
              relacionamentos e paixões, e adaptava os símbolos visuais para a
              realidade local. Inclusive, a banda Metrô, conhecida pelo hit{' '}
              <a
                href="https://www.youtube.com/watch?v=SYIeF9PP2_0"
                target="_blank"
                rel="noreferrer"
              >
                <i>"Beat acelerado"</i>
              </a>
              , de 1984, anunciou o retorno em 2015 e continua somando plays no
              YouTube até hoje.
            </p>
          </S.Text>
        </S.Content>
        <S.Content className="right">
          <S.Photo2
            className="wow animate__animated animate__zoomIn"
            data-wow-duration="1s"
          />
          <S.Legend>
            Jaqueta, Saint Laurent. Brincos, colares e bracelete (usado como
            colar), Missoma. Aneis, Cartier. Bottom, Underground England.
          </S.Legend>
        </S.Content>
      </S.Wrapper>
      <S.Wrapper>
        <S.Content className="left">
          <S.Photo3
            className="wow animate__animated animate__zoomIn"
            data-wow-duration="1s"
          />
          <S.Legend>Camisa, Saint Laurent. Brincos, Missoma.</S.Legend>
        </S.Content>
        <S.Content className="right text">
          <S.Text
            className="wow animate__animated animate__fadeIn"
            data-wow-duration="1s"
            data-wow-delay="400ms"
          >
            <S.Title>Movimento bold</S.Title>
            <p>
              Na época do nascimento do New Romantic, os góticos também marcavam
              presença, influenciados pela vibe do vocalista Robert Smith, do
              The Cure. A alfaiataria surge como resposta aos anos punk e suas
              roupas cheias de alfinetes e rebites. Ainda assim, a androginia e
              a estranheza do rock não saem de cena. Dessa vez, ela assume
              contornos radicais no cabelo e na maquiagem. As referências
              visuais flutuam entre diferentes eras históricas, do período
              romântico inglês (séculos 18 e 19) ao construtivismo russo do
              início do século 20, passando pelas décadas de 1930 e 20.{' '}
            </p>

            <p>
              A Blitz era point dos alunos de moda da tradicional faculdade
              Central St. Martins, e só entrava quem tivesse caprichado na
              montação. Em 1980, David Bowie recrutou alguns frequentadores da
              noite para participar do videoclipe da música “Ashes to ashes”, do
              disco Scary monsters (and super creeps) do mesmo ano. Todos de
              pierrô, assim como o artista, eles fizeram história. Aliás, essa
              foi a primeira vez que Boy George apareceu em um clipe! Ele era um
              dos integrantes convidados pelo casting do ídolo.
            </p>
          </S.Text>
        </S.Content>
      </S.Wrapper>
      <S.Wrapper className="right">
        <S.Content className="left text">
          <S.Text
            className="right wow animate__animated animate__fadeIn"
            data-wow-duration="1s"
            data-wow-delay="400ms"
          >
            <S.Title>Som e visão</S.Title>
            <p>
              Se Londres tinha um circuito restrito de clubes na virada da
              década, já na metade dos anos 1990, a cena clubber se espalhava
              pela cidade e pelo país. Ao passo que o Reino Unido deixava a
              recessão, o Thatcherismo (1979-1990) ascendia, assim como o
              conservadorismo, então, como uma válvula de escape da vida
              cotidiana, a vida noturna se expandia.{' '}
            </p>

            <p>
              Entre encontros e desencontros com o punk, o New Romantic
              acompanhava a tecnologia disponível na época – sintetizadores e
              música eletrônica –, enquanto desafiava as normas como uma
              tendência extravagante para todos os corpos. Símbolos como os
              cabelos geométricos e cheios de pontas foram readaptados à nova
              década, da mesma forma que as makes ganharam cores e formas ainda
              mais chamativas. Mais de 40 anos depois, especialmente no período
              após um grande trauma a nível global, a maquiagem voltou ao lugar
              de experimentação com muito glamour e drama na equação.
            </p>
          </S.Text>
        </S.Content>
        <S.Content className="right">
          <S.Photo4
            className="wow animate__animated animate__zoomIn"
            data-wow-duration="1s"
          />
          <S.Legend>
            Jaqueta, Saint Laurent. Brincos, Cartier. Colares, Missoma. Bottoms,
            Underground England
          </S.Legend>
        </S.Content>
      </S.Wrapper>
      <S.Wrapper>
        <S.Content className="left">
          <S.Photo5
            className="wow animate__animated animate__zoomIn"
            data-wow-duration="1s"
          />
          <S.Legend>
            Jaqueta, Saint Laurent. Brincos, Le Monde Beryl. Anel, Cartier
          </S.Legend>
        </S.Content>
        <S.Content className="right text">
          <S.Text
            className="wow animate__animated animate__fadeIn"
            data-wow-duration="1s"
            data-wow-delay="400ms"
          >
            <S.Title>Novíssimos românticos</S.Title>
            <p>
              Conforme o tempo foi passando, o movimento inglês foi sendo
              diluído e se misturando a novas ideias. Em 2014, a cantora Taylor
              Swift lançou a empolgante faixa “New romantics” como bônus track
              do disco 1989, com claras referências ao synthpop da década de
              1980, e ela foi eleita como a segunda melhor música da carreira da
              artista pela revista{' '}
              <a
                href="https://www.rollingstone.com/music/music-lists/taylor-swift-songs-ranked-rob-sheffield-201800/delicate-2017-202724/"
                target="_blank"
                rel="noreferrer"
              >
                <i>Rolling Stone.</i>
              </a>
            </p>

            <p>
              No ano passado, Miley Cyrus lançou o disco <i>Plastic hearts</i>,
              no qual conta com a participação de uma série de ídolos, como
              Billy Idol, Joan Jett e Stevie Nicks. Como uma grande homenagem à
              música das décadas de 1970 e 80, Miley entende que dessa forma
              pode apresentar a música que ama para os seus fãs mais novos.
              Especialmente no visual, a cantora estadunidense se inspirou no
              glamour para compor o seu atual momento artístico, do cabelo com
              mullet muito repicado aos looks com brilhos, muita gente passou a
              cogitar repetir o corte depois de assistir a seus videoclipes.{' '}
            </p>

            <p>
              Homenageando outra era da música, especialmente o pop punk e o
              rock dos anos 2000, Willow Smith também trouxe alguns ídolos para
              colaborar no seu disco, <i>lately i feel EVERYTHING</i>, lançado
              em julho. O baterista do Blink 182, Travis Barker, e Avril Lavigne
              são alguns dos feats no trabalho da compositora. A estética Y2K de
              Willow é menos extrovertida do que o revival da Miley, mas não
              deixa de interpretar alguns sinais já conhecidos. E você? O que
              está esperando para entrar nessa também?
            </p>
          </S.Text>
        </S.Content>
      </S.Wrapper>
      <S.Credits>
        Edição de moda: Georgia Madley
        <br />
        Cabelo: Jake Gallagher (The Only Agency)
        <br />
        Maquiagem: Florrie White (Bryiant Artists)
      </S.Credits>
    </S.Container>
  );
};

export default Main;
